import { useState } from "react";
import { RefreshCw, TriangleAlert, RotateCcw } from "lucide-react";
import { Button } from "../../components/ui/button";
import { isChunkLoadError } from "../lazyWithRetry";
import { useDocumentTitle } from "../hooks/useDocumentTitle";

// RouteErrorPage — the router-wide `defaultErrorComponent`. Anything that
// throws out of a route component (a failed code-split chunk after a deploy,
// a render crash, a thrown loader) lands here instead of TanStack Router's
// bare "Something went wrong!" fallback.
//
// Two shapes:
//   • chunk-load failure  → the app was redeployed while this tab was open,
//     so a dynamic import points at a hash that no longer exists. This is
//     recoverable: a full reload fetches the fresh asset map. We present it
//     as a calm "new version available" prompt, not a crash.
//   • genuine error       → real bug / unexpected failure. Show a clear
//     message, a collapsible technical detail, and reset / reload actions.

interface RouteErrorPageProps {
    error: Error;
    reset?: () => void;
}

export function RouteErrorPage({ error, reset }: RouteErrorPageProps) {
    const isChunk = isChunkLoadError(error);
    useDocumentTitle(isChunk ? "Update available" : "Something went wrong");

    if (isChunk) {
        return (
            <main className="flex min-h-[60vh] w-full items-center justify-center p-6">
                <div className="flex w-full max-w-md flex-col items-center gap-5 rounded-xl border bg-card p-8 text-center shadow-sm">
                    <div className="flex size-12 items-center justify-center rounded-full bg-muted text-foreground">
                        <RefreshCw className="size-6" aria-hidden="true" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <h1 className="text-lg font-semibold text-foreground">A new version is available</h1>
                        <p className="text-sm text-muted-foreground">
                            This page was updated while your tab was open, so part of it could not load.
                            Reload to get the latest version — your session stays signed in.
                        </p>
                    </div>
                    <Button size="lg" onClick={() => window.location.reload()}>
                        <RefreshCw className="size-4" aria-hidden="true" />
                        Reload now
                    </Button>
                </div>
            </main>
        );
    }

    return <GenericError error={error} reset={reset} />;
}

function GenericError({ error, reset }: RouteErrorPageProps) {
    const [showDetail, setShowDetail] = useState(false);

    return (
        <main className="flex min-h-[60vh] w-full items-center justify-center p-6">
            <div className="flex w-full max-w-lg flex-col items-center gap-5 rounded-xl border bg-card p-8 text-center shadow-sm">
                <div className="flex size-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                    <TriangleAlert className="size-6" aria-hidden="true" />
                </div>
                <div className="flex flex-col gap-1.5">
                    <h1 className="text-lg font-semibold text-foreground">Something went wrong</h1>
                    <p className="text-sm text-muted-foreground">
                        An unexpected error stopped this page from loading. You can try again, or
                        reload if the problem persists.
                    </p>
                </div>

                <div className="flex flex-wrap items-center justify-center gap-2">
                    {reset ? (
                        <Button size="lg" onClick={reset}>
                            <RotateCcw className="size-4" aria-hidden="true" />
                            Try again
                        </Button>
                    ) : null}
                    <Button size="lg" variant="outline" onClick={() => window.location.reload()}>
                        <RefreshCw className="size-4" aria-hidden="true" />
                        Reload page
                    </Button>
                </div>

                <button
                    type="button"
                    onClick={() => setShowDetail((v) => !v)}
                    className="text-xs font-medium text-muted-foreground underline-offset-2 hover:underline"
                >
                    {showDetail ? "Hide details" : "Show details"}
                </button>
                {showDetail ? (
                    <pre className="max-h-48 w-full overflow-auto rounded-lg bg-muted p-3 text-left text-xs text-muted-foreground">
                        {error?.message || String(error)}
                        {error?.stack ? `\n\n${error.stack}` : ""}
                    </pre>
                ) : null}
            </div>
        </main>
    );
}
